'use client'

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { usePathname } from '@/hooks/usePathname'
import { useStatus } from '@powersync/react'
import { useTranslations } from '@/hooks/useTranslations'
import { useAuth } from '@/components/AuthProvider'
import { hasEverSynced, markEverSynced } from '@/lib/boot-state'
import { isMarketingPath } from '@/lib/route-gates'
import { SplashScreen } from '@/components/SplashScreen'
import { logPsLifecycle } from '@/lib/powersync/lifecycle-log'

// Hard cap on the splash — if PowerSync never lands, release anyway.
// Covers: PowerSync backend stalls or any edge where sync stays stuck.
// User always gets to the app within this window.
const GATE_TIMEOUT_MS = 60_000

// Stable window for `!downloading` before we treat sync as truly idle. Short
// enough that real users don't notice; long enough to bridge the few-ms gaps
// PowerSync hits between checkpoints.
const SYNC_IDLE_DEBOUNCE_MS = 500

/**
 * Variant 2 splash: blocks render on first-ever login (per user, per device)
 * until PowerSync has completed its first sync. On every subsequent reload
 * the localStorage flag is set → renders children immediately → local-first
 * feel preserved.
 *
 * Self-contained: mount anywhere in the authed tree, un-mount to disable.
 * Reads from useStatus (PowerSync) only — no SW plumbing.
 *
 * Outer wrapper bails on unauthenticated tree (login, marketing pages) so
 * useStatus() — which crashes with `Cannot read properties of null` when no
 * PowerSyncContext.Provider is above it (see PowerSyncProvider.tsx:27) —
 * only ever runs inside the authed branch.
 */
export function AppBootGate({ children }: { children: ReactNode }) {
  const { user, loading: authLoading, isOffline, connectivityVerified } = useAuth()
  const pathname = usePathname()
  // Auth-entry / public routes (setup-password, login, ticket-submit, legal)
  // must never gate: on /setup-password the user flips to authed mid-render
  // after setSession(hash tokens), which otherwise raises a 60s splash over
  // the password form and breaks the magic-link flow.
  if (isMarketingPath(pathname)) return <>{children}</>
  if (authLoading || !user?.id) return <>{children}</>
  // Offline: a first sync can't complete, so gating only produces a 60s dead
  // splash. We still mount GatedInner (and just never gate while offline) so the
  // offline<->online flip does NOT swap the component type at this position.
  // Swapping it (Fragment <-> GatedInner) remounts the entire authed shell on
  // every reconnect — which blows away in-progress work, e.g. a half-typed
  // defect note while a capture is open. GatedInner short-circuits to children
  // when offline; SyncStatusPill surfaces sync progress once connectivity is
  // back. navigator.onLine covers the true offline cold start before
  // ConnectivityService settles isOffline.
  //
  // Until the first probe settles, isOffline is just the pessimistic native
  // seed — treating it as real disabled the gate on every cold start, so the
  // children flashed for ~0.5s before the probe landed and the splash
  // re-engaged. Pre-verification we trust navigator.onLine alone; a truly
  // offline start still un-gates within one probe (≤4s) once verified.
  const offline = (connectivityVerified && isOffline)
    || (typeof navigator !== 'undefined' && !navigator.onLine)
  return <GatedInner userId={user.id} offline={offline}>{children}</GatedInner>
}

function GatedInner({ userId, offline, children }: { userId: string; offline: boolean; children: ReactNode }) {
  const status = useStatus()
  const t = useTranslations('sync')
  const [timedOut, setTimedOut] = useState(false)
  const [syncIdleStable, setSyncIdleStable] = useState(false)
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)

  // PowerSync's downloadProgress is PER sync iteration, not cumulative: its
  // counts/fraction are `untilPriority(FULL_SYNC_PRIORITY)` measured relative
  // to the LAST completed checkpoint (SyncProgress sums target_count - at_last).
  // A first sync streams several checkpoints, so the raw fraction sawtooths —
  // fills to ~100%, then rebaselines toward 0 as the next checkpoint's target
  // lands, then climbs again. That's the "bar goes full, then empty, then 80%,
  // then loads" the user hit on the large prod sync. The SDK can't give us the
  // grand total up front, so we render a MONOTONIC bar: only ever advance, and
  // hold (never reset) when downloadProgress clears between checkpoints / during
  // the apply phase. Capped just under 100% — the splash unmounts on hasSynced,
  // so we never need to paint a full bar, and a bar that only climbs reads as
  // honest progress where the raw sawtooth reads as broken.
  const PROGRESS_CAP = 0.97
  const dl = status.downloadProgress
  const shownFractionRef = useRef(0)
  useEffect(() => {
    if (!dl) return
    const next = Math.min(PROGRESS_CAP, dl.downloadedFraction)
    if (next <= shownFractionRef.current) return // never go backwards
    shownFractionRef.current = next
    setProgress({ done: Math.round(next * 1000), total: 1000 })
    // Key on the fraction value, not `dl`: every status tick yields a fresh
    // SyncProgress instance and depending on it would re-run per tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dl?.downloadedFraction])

  // First-sync milestone trace into the lifecycle ring buffer (survives native
  // reloads, readable in DebugDiagnostics without Web Inspector). Sizes WHERE
  // the ~19s first-sync stall sits: connect handshake vs. download stream vs.
  // apply phase — and whether ANY incremental downloadProgress events arrive
  // (if the stream is WKWebView-buffered they won't: we'd jump straight from
  // connected to hasSynced with no progress ticks). Logs transitions only.
  const mountRef = useRef<number>(Date.now())
  const prevRef = useRef({ connected: false, sawProgress: false, hasSynced: false, lastTickTs: 0, lastDone: -1 })
  useEffect(() => {
    const el = Date.now() - mountRef.current
    const p = prevRef.current
    if (status.connected !== p.connected) {
      p.connected = status.connected
      logPsLifecycle('status', `boot +${el}ms connected=${status.connected}`)
    }
    if (dl && !p.sawProgress) {
      p.sawProgress = true
      logPsLifecycle('status', `boot +${el}ms first-progress ${dl.downloadedOperations}/${dl.totalOperations}`)
    }
    // Throttled per-tick progress curve: reveals WHERE the first-sync stall
    // sits. If downloadedOperations climbs steadily across the gap → slow
    // download (network/backend or WKWebView stream buffering). If it pins at
    // totalOperations early but hasSynced lags → the native SQLite apply phase
    // is the bottleneck. Throttle to 250ms to resolve the fine curve of the
    // ~5.4s WebSocket sync (≈21 ticks fits the 40-entry ring buffer): tells
    // "incremental over the whole window" apart from "delay then end-burst".
    if (dl && dl.downloadedOperations !== p.lastDone && Date.now() - p.lastTickTs > 250) {
      p.lastTickTs = Date.now()
      p.lastDone = dl.downloadedOperations
      logPsLifecycle('status', `boot +${el}ms progress ${dl.downloadedOperations}/${dl.totalOperations} downloading=${status.dataFlowStatus?.downloading}`)
    }
    if (status.hasSynced && !p.hasSynced) {
      p.hasSynced = true
      const ops = dl ? `${dl.downloadedOperations}/${dl.totalOperations}` : 'no-progress-events'
      logPsLifecycle('status', `boot +${el}ms hasSynced (${ops})`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.connected, status.hasSynced, dl?.downloadedOperations, dl?.totalOperations])

  useEffect(() => {
    const id = setTimeout(() => setTimedOut(true), GATE_TIMEOUT_MS)
    return () => clearTimeout(id)
  }, [])

  // hasSynced is the FIRST-checkpoint latch, not "all streams settled". Heavy
  // streams (analyses, defect_reports) keep flowing past it, so also wait for
  // !downloading — with a stable window to absorb the few-ms gaps between
  // checkpoints, otherwise the splash dismisses and the pill immediately
  // re-spins as "Syncing…".
  const downloading = status.dataFlowStatus?.downloading
  useEffect(() => {
    if (hasEverSynced(userId)) return
    if (!status.hasSynced || downloading) {
      setSyncIdleStable(false)
      return
    }
    const id = setTimeout(() => setSyncIdleStable(true), SYNC_IDLE_DEBOUNCE_MS)
    return () => clearTimeout(id)
  }, [userId, status.hasSynced, downloading])

  // Never gate while offline — a first sync can't land, so gating would only
  // paint a dead splash. GatedInner stays mounted across the reconnect so the
  // authed shell (and any in-progress work) survives the flip.
  const shouldGate = !offline && !hasEverSynced(userId) && !syncIdleStable && !timedOut

  useEffect(() => {
    if (!syncIdleStable) return
    if (!hasEverSynced(userId)) markEverSynced(userId)
  }, [userId, syncIdleStable])

  if (!shouldGate) return <>{children}</>

  // Progress-staged copy: keys off the same monotonic fraction that drives the
  // bar (0 before any download event → "setup"). >70% covers the apply-phase
  // tail near the 97% cap, so "almost ready" reads honestly while it sits there.
  const frac = progress ? progress.done / progress.total : 0
  const label =
    frac >= 0.7 ? t('firstSyncAlmost') : frac >= 0.3 ? t('firstSyncLoading') : t('firstSyncSetup')

  return (
    <>
      <div aria-hidden="true" style={{ visibility: 'hidden' }}>{children}</div>
      <SplashScreen label={label} progress={progress} />
    </>
  )
}
